import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { formatDate } from "../../lib/format";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Archive, Loader2, ArchiveRestore, Search } from "lucide-react";
import { toast } from "sonner";

const norm = (s) => (s || "").toString().toLowerCase();

export default function ArchivioPratiche() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/studio/pratiche/archiviate");
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const ripristina = async (p) => {
    if (!window.confirm(`Ripristinare la pratica ${p.numero_pratica} nella lista principale?`)) return;
    setBusy(p.id);
    try {
      await api.post(`/studio/pratiche/${p.id}/ripristina`);
      window.dispatchEvent(new Event("pgp-archivio-changed"));
      toast.success("Pratica ripristinata");
      setRows((rs) => rs.filter((r) => r.id !== p.id));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(null); }
  };

  const filtered = rows.filter((p) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    try { return norm(JSON.stringify(p)).includes(q); } catch { return true; }
  });

  return (
    <div data-testid="archivio-page">
      <PageHeader
        title="Archivio Pratiche"
        subtitle="Pratiche chiuse archiviate. Consultabili e ricercabili in sola lettura; puoi ripristinarle nella lista principale."
        icon={Archive}
      />

      <div className="pgp-card p-3 mb-4" data-testid="archivio-filters">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="archivio-search"
            placeholder="Cerca in archivio: numero, assistito, targa, compagnia…"
            className="pl-8 bg-slate-900/60 border-slate-700" />
        </div>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
      ) : filtered.length === 0 ? (
        <div className="pgp-card p-10 text-center text-slate-400" data-testid="archivio-empty">
          <Archive className="h-8 w-8 mx-auto mb-3 text-slate-600" />
          {rows.length === 0 ? "Nessuna pratica archiviata." : "Nessun risultato per la ricerca."}
        </div>
      ) : (
        <div className="pgp-card overflow-x-auto" data-testid="archivio-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
                <th className="px-4 py-3 font-mono">N° Pratica</th>
                <th className="px-4 py-3">Assistito</th>
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3">Data Sinistro</th>
                <th className="px-4 py-3">Archiviata il</th>
                <th className="px-4 py-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-b border-slate-800/60 hover:bg-slate-800/30" data-testid={`archivio-row-${p.id}`}>
                  <td className="px-4 py-3 font-mono text-amber-400">{p.numero_pratica}</td>
                  <td className="px-4 py-3 text-slate-100">{p.nome_assistito || p.assistito_ragione_sociale || "—"}</td>
                  <td className="px-4 py-3 text-slate-300">{p.tipo || "—"}</td>
                  <td className="px-4 py-3 text-slate-300 font-mono">{p.data_sinistro ? formatDate(p.data_sinistro) : "—"}</td>
                  <td className="px-4 py-3 text-slate-400 font-mono">{p.archiviata_at ? formatDate(p.archiviata_at) : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" onClick={() => ripristina(p)} disabled={busy === p.id} data-testid={`archivio-restore-${p.id}`}
                      className="bg-teal-600 hover:bg-teal-700 text-white">
                      {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ArchiveRestore className="h-4 w-4 mr-1.5" /> Ripristina</>}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
